import { Box, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { useViewContext } from '../hooks/useViewContext';

const MAX_DOCUMENT_NAME_LENGTH = 24;
const MAX_SECTION_TITLE_LENGTH = 30;

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}...`;
}

/** Row above the input showing what the assistant currently has as context (document/section or project). */
export function AIChatContextBanner() {
  const { documentName, documentSectionId, documentSectionTitle, documentSectionIsRoot, projectName } = useViewContext();

  const hasSection = documentSectionId != null && !!documentSectionTitle;
  // A non-root section has ancestors in between — "..." stands in for them
  // since we only carry the selected section's own id/title, not its path.
  const separator = documentSectionIsRoot === false ? '→ ... →' : '→';

  let label: string | null;
  if (hasSection) {
    label = documentName
      ? `${truncate(documentName, MAX_DOCUMENT_NAME_LENGTH)} ${separator} ${truncate(documentSectionTitle!, MAX_SECTION_TITLE_LENGTH)}`
      : truncate(documentSectionTitle!, MAX_SECTION_TITLE_LENGTH);
  } else {
    label = documentName ?? projectName;
  }

  if (!label) return null;

  const Icon = documentName || hasSection ? DescriptionOutlinedIcon : FolderOutlinedIcon;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 0.75,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Icon sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0 }} />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {label}
      </Typography>
    </Box>
  );
}
